import axios from 'axios';
import { bandByKey } from './bands';

const username = 'app';
const password = '$CaS}~ZNS-`AnEHx9Z($@45z';
const basicAuth = 'Basic ' + Buffer.from(username + ':' + password).toString('base64');

const http = axios.create({ withCredentials: true, headers: { Pragma: 'no-cache', Authorization: basicAuth } });

// panicstream files require this key appended to every stream URL
const WSP_STREAM_KEY = '?key=9721766773F38DC2BD714E3CA0CC751C';

// API origin override: set only for the e2e harness build (contract G.4,
// env var REACT_APP_ + API_ORIGIN); production builds leave it unset and talk
// to the band port. The name is joined at runtime on purpose. CRA inlines
// process.env as an object literal, so a plain dotted access would leave the
// variable's name in every production bundle and its source map, and the
// G.2 pre-deploy grep guard would refuse the build. Keep the full name out of
// this file, comments included.
const API_ORIGIN_VAR = ['REACT', 'APP', 'API', 'ORIGIN'].join('_');
const API_ORIGIN = process.env[API_ORIGIN_VAR] || '';
const base = (band) => (API_ORIGIN ? API_ORIGIN : 'https://whsmacon.com:' + band.port) + '/' + band.key;

const pad2 = (n) => String(n).padStart(2, '0');

// mm:ss (or hh:mm) strings summed into an hh:mm:ss set length — WSP tapes
// don't carry a precomputed length, so it's derived from the song list.
const sumLengths = (songs) => {
  let total = 0;
  Object.keys(songs).forEach((key) => {
    const time = songs[key].length || '0:0';
    total += (parseInt(time.split(':')[0], 10) || 0) * 60;
    total += parseInt(time.split(':')[1], 10) || 0;
  });
  const h = Math.floor(total / 3600);
  const m = Math.floor((total - h * 3600) / 60);
  const s = total - h * 3600 - m * 60;
  return pad2(h) + ':' + pad2(m) + ':' + pad2(s);
};

const venueFromTitle = (band, title) => {
  const m = band.titleMatch && title ? title.match(band.titleMatch) : null;
  return m ? m[1] : 'Unknown';
};

const wspSongUrl = (file) => file.replace('http://', 'https://') + WSP_STREAM_KEY;

// Same rule as the server's lib/songnorm wspTitleFromFile: last path segment,
// no query or fragment, URL-decoded, '.mp3' dropped, leading track number
// dropped (the row's `no` already shows it), trimmed. The old split('.') cut
// "03 Mr. Soul" to "03 Mr".
const wspSongName = (file) => {
  const path = String(file == null ? '' : file).split('?')[0].split('#')[0];
  let name = path.substring(path.lastIndexOf('/') + 1);
  try {
    name = decodeURIComponent(name);
  } catch (e) {
    name = name.replace(/%20/g, ' ');
  }
  return name.replace(/\.mp3$/i, '').replace(/^\d{1,3}[\s._-]+/, '').trim();
};

// ---- canonical adapters ------------------------------------------------
// Everything downstream of api.js works with these shapes:
//   show:  { dateKey, dateApi, displayDate, venue, location, length }
//   tape:  { id, label, sbd, rating, downloads, taper, source, description, length }
//   set:   { tracks: [{ no, title, length, url }], setLength }

const adaptShow = (band, item) => {
  if (band.wsp) {
    const dateKey = item.date.substring(0, 10);
    let venue = 'Unknown';
    let location = 'Unknown';
    if (item.set && item.set.length > 0) {
      const v = item.set[0].venue || {};
      if (v.name) venue = v.name;
      if (v.city && v.city.name) location = v.city.name;
      if (v.city && v.city.state) location += ', ' + v.city.state;
    }
    return { dateKey, dateApi: dateKey, displayDate: dateKey, venue, location, length: sumLengths(item.songs || {}) };
  }
  return {
    dateKey: item.date,
    dateApi: item.date,
    displayDate: item.date.substring(0, 10),
    venue: venueFromTitle(band, item.title),
    location: item.coverage || 'Unknown',
    length: item.maxLength,
  };
};

const dedupeByDate = (shows) => {
  const seen = {};
  return shows.filter((s) => (seen[s.dateKey] ? false : (seen[s.dateKey] = true)));
};

// A few archive docs have no date (2 Billy Strings items from 2021). The
// adapters slice `date`, so one such doc used to break the whole year list.
// Raw docs without a string date are dropped before adapting.
const hasDate = (item) => !!item && typeof item.date === 'string' && item.date.length >= 10;
const listOf = (data) => (Array.isArray(data) ? data : []);

const adaptTape = (band, item) => {
  if (band.wsp) {
    return { id: item._id, label: item.date, length: sumLengths(item.songs || {}), sbd: false };
  }
  const subject = item.subject || [''];
  return {
    id: item.identifier,
    sbd: subject.includes('Soundboard') || item.source === 'Soundboard',
    rating: item.avg_rating ? Math.round(item.avg_rating * 2 * 100) / 100 : null,
    downloads: item.downloads,
    taper: item.publisher,
    source: item.source,
    description: item.description,
    length: item.setLength,
  };
};

const adaptSet = (band, data) => {
  if (band.wsp) {
    const tracks = Object.keys(data.songs || {}).map((key) => ({
      no: key,
      title: wspSongName(data.songs[key].file),
      length: data.songs[key].length,
      url: wspSongUrl(data.songs[key].file),
    }));
    return { tracks, setLength: sumLengths(data.songs || {}) };
  }
  const tracks = (data.tracks || []).map((t) => ({ no: t.track, title: t.songName, length: t.length, url: t.download }));
  return { tracks, setLength: data.setLength };
};

// ---- fetchers ----------------------------------------------------------

export const fetchYears = (band) => http.get(base(band) + '/years/').then((r) => r.data);

export const fetchShows = (band, year) => {
  const path = band.wsp ? '/show/' + year : '/year/' + year;
  return http.get(base(band) + path).then((r) => dedupeByDate(listOf(r.data).filter(hasDate).map((item) => adaptShow(band, item))));
};

export const fetchTapes = (band, dateApi) => {
  const path = band.wsp ? '/show/' + dateApi : '/date/' + dateApi;
  return http.get(base(band) + path).then((r) => listOf(r.data).filter(hasDate).map((item) => adaptTape(band, item)));
};

export const fetchSet = (band, id) => http.get(base(band) + '/id/' + id).then((r) => adaptSet(band, r.data));

const optionList = (values) => values.map((v) => ({ value: v, label: v }));

export const fetchSongs = (band) => http.get(base(band) + '/songs/').then((r) => optionList(r.data));
// Legacy venue-name list (/venue/). `fetchVenues` below is the Set Search catalog.
export const fetchVenueNames = (band) => http.get(base(band) + '/venue/').then((r) => optionList(r.data));
export const fetchLocations = (band) => http.get(base(band) + '/location/').then((r) => optionList(r.data));

// Search results come back as show documents with an embedded `tapes` array.
const adaptSearchShow = (band, item) => {
  const tape = item.tapes[0];
  if (band.wsp) {
    const dateKey = tape.date.substring(0, 10);
    let venue = 'Unknown';
    let location = 'Unknown';
    if (item.venue && item.venue.name) venue = item.venue.name;
    if (item.venue && item.venue.city && item.venue.city.name) {
      location = item.venue.city.name;
      if (item.venue.city.state) location += ', ' + item.venue.city.state;
    }
    return { dateKey, dateApi: dateKey, displayDate: dateKey, venue, location, length: sumLengths(tape.songs || {}) };
  }
  let maxLength = '00:00:00';
  item.tapes.forEach((t) => {
    if (t.setLength > maxLength) maxLength = t.setLength;
  });
  return {
    dateKey: tape.date,
    dateApi: tape.date,
    displayDate: tape.date.substring(0, 10),
    venue: venueFromTitle(band, tape.title),
    location: tape.coverage || 'Unknown',
    length: maxLength,
  };
};

export const searchShows = (band, kind, value) =>
  http
    .get(base(band) + '/search/' + kind + '/' + encodeURIComponent(value))
    .then((r) =>
      dedupeByDate(
        listOf(r.data)
          .filter((item) => item && Array.isArray(item.tapes) && item.tapes.length > 0 && hasDate(item.tapes[0]))
          .map((item) => adaptSearchShow(band, item))
      )
    );

// ---- Set Search + Vibe (contract D.3) ------------------------------------
// Every function below resolves to the adapted shape, or rejects with an
// Error whose `.userMessage` is ready-to-show copy (contract D.7). The Error
// also carries `.status` (HTTP status, 0 for network), `.code` (the server's
// error code, or 'network') and `.detail` (server detail text or null).
// Query strings are built with URLSearchParams (repeated keys, encoded
// values); never axios `params`, which would send `song[]=`.
// `band` may be a band object from bands.js or a band key string.

const DS_TIMEOUT_MS = 20000;

const RATE_LIMITED_COPY = 'Too many requests. Wait a few seconds and try again.';

const ERROR_COPY = {
  catalog: (b) => 'Could not load the song list for ' + b.name + '. Search is off for now.',
  find: () => 'Could not run that search. Try again in a moment.',
  song: () => 'Could not load details for this song.',
  show: () => 'Could not load details for this show.',
  surprise: () => 'Could not pick a show right now.',
  radio: () => 'The radio could not load more songs.',
  vibe: () => 'Vibe Check is resting right now. The filters below still work.',
};

const resolveBand = (band) => (typeof band === 'string' ? bandByKey(band) : band);

const toApiError = (err, b, kind) => {
  if (err && err.userMessage) return err;
  const res = err && err.response;
  const status = res ? res.status : 0;
  const body = res && res.data && typeof res.data === 'object' ? res.data : {};
  const bandLike = b || { name: 'this band' };
  let msg;
  if (status === 429) msg = RATE_LIMITED_COPY;
  else if (kind === 'vibe') msg = ERROR_COPY.vibe(bandLike); // never surface not_ready etc. for Vibe Check
  else if (status === 503 && body.error === 'not_ready') msg = 'Search for ' + bandLike.name + ' is still being built. Try again later.';
  else if (kind === 'surprise' && status === 404) msg = 'No shows match these settings.';
  else msg = ERROR_COPY[kind](bandLike);
  const e = new Error(msg);
  e.userMessage = msg;
  e.status = status;
  e.code = typeof body.error === 'string' ? body.error : res ? 'http_' + status : 'network';
  e.detail = typeof body.detail === 'string' ? body.detail : null;
  e.cause = err;
  return e;
};

// Build URLSearchParams from a plain object: null / undefined / '' / false are
// skipped, true becomes '1', arrays become repeated keys.
const toParams = (obj) => {
  const p = new URLSearchParams();
  Object.keys(obj || {}).forEach((k) => {
    const v = obj[k];
    if (v == null || v === '' || v === false) return;
    if (Array.isArray(v)) {
      v.forEach((x) => {
        if (x != null && x !== '') p.append(k, String(x));
      });
    } else {
      p.append(k, v === true ? '1' : String(v));
    }
  });
  return p;
};

const dsGet = (band, path, params, kind, adapt) => {
  const b = resolveBand(band);
  if (!b) return Promise.reject(toApiError(null, null, kind));
  const qs = params ? params.toString() : '';
  return http
    .get(base(b) + '/ds' + path + (qs ? '?' + qs : ''), { timeout: DS_TIMEOUT_MS })
    .then((r) => (adapt ? adapt(r.data, b) : r.data))
    .catch((err) => {
      throw toApiError(err, b, kind);
    });
};

const clampInt = (v, lo, hi, dflt) => {
  const n = parseInt(v, 10);
  if (!Number.isFinite(n)) return dflt;
  return Math.min(hi, Math.max(lo, n));
};

// Server-side caps on repeated find params (contract B.3). The builder UI
// should keep queries inside these; exported so the search components can.
export const FIND_LIMITS = { song: 12, seq: 4, seqKeys: 5, not: 4, slot: 6, sandwich: 3, jam: 4 };

// FindQuery (contract D.1) -> URLSearchParams for /ds/find, /find/count and
// /find/matches. `withSort: false` for find/count.
export const toFindParams = (q, { withSort = true } = {}) => {
  const p = new URLSearchParams();
  if (!q) return p;
  const songs = Array.isArray(q.songs) ? q.songs : [];
  const links = Array.isArray(q.links) ? q.links : [];
  const ok = (s) => !!(s && typeof s.key === 'string' && s.key);

  songs.forEach((s) => ok(s) && p.append('song', s.key));

  // every maximal run of links[i] === true is one seq=k1>k2>...
  let run = null;
  songs.forEach((s, i) => {
    if (!ok(s)) {
      run = null;
      return;
    }
    if (!run) run = [s.key];
    const next = songs[i + 1];
    if (i < songs.length - 1 && links[i] === true && ok(next)) {
      run.push(next.key);
    } else {
      if (run.length > 1) p.append('seq', run.join('>'));
      run = null;
    }
  });

  songs.forEach((s) => ok(s) && s.slot && s.slot !== 'any' && p.append('slot', s.slot + ':' + s.key));
  songs.forEach((s) => ok(s) && (s.jam === 75 || s.jam === 90) && p.append('jam', s.key + ':' + s.jam));
  (Array.isArray(q.not) ? q.not : []).forEach((n) => ok(n) && p.append('not', n.key));
  (Array.isArray(q.sandwiches) ? q.sandwiches : []).forEach(
    (w) => w && ok(w.outer) && ok(w.inner) && p.append('sandwich', w.outer.key + '~' + w.inner.key)
  );
  if (q.venue && q.venue.id) p.append('venue', q.venue.id);
  if (q.city && q.city.key) p.append('city', q.city.key);
  if (Array.isArray(q.years) && q.years.length === 2) {
    if (q.years[0] != null) p.append('from', String(q.years[0]));
    if (q.years[1] != null) p.append('to', String(q.years[1]));
  }
  if (q.era) p.append('era', q.era);
  if (q.sbd) p.append('sbd', '1');
  if (q.vibe) p.append('vibe', q.vibe);
  if (q.mode === 'min') p.append('mode', 'min:' + clampInt(q.minN, 1, 12, 2));
  else if (q.mode === 'closest') p.append('mode', 'closest');
  else p.append('mode', 'all');
  if (withSort) p.append('sort', q.sort || 'date');
  return p;
};

// Playable URL for a Segment track `file` (contract B.0 stream convention).
export const streamUrl = (bandKey, file) => (bandKey === 'wsp' ? wspSongUrl(file) : file);

// ---- catalog ----

export const fetchMeta = (band) => dsGet(band, '/catalog/meta', null, 'catalog');

export const fetchCatalogSongs = (band) =>
  dsGet(band, '/catalog/songs', null, 'catalog', (data) =>
    (data.songs || []).map((s) => ({ ...s, value: s.key, label: s.name + ' (' + s.plays + ')' }))
  );

export const fetchVenues = (band) =>
  dsGet(band, '/catalog/venues', null, 'catalog', (data) =>
    (data.venues || []).map((v) => ({ ...v, value: v.id, label: v.city ? v.name + ', ' + v.city : v.name }))
  );

// label: the server's B.1 place label ("Ithaca, NY", "Hamburg, Germany");
// only rebuilt client-side if a row lacks it, so "City, 04" never shows.
export const fetchCities = (band) =>
  dsGet(band, '/catalog/cities', null, 'catalog', (data) =>
    (data.cities || []).map((c) => ({
      ...c,
      value: c.key,
      label: c.label || [c.city, c.state || c.country].filter(Boolean).join(', '),
    }))
  );

// ---- Set Search ----

// FindShow = the browse show shape { dateKey, dateApi, displayDate, venue,
// location, length } plus every ShowSummary / find field (sid, iso, matched,
// hits, of, explain, bestTape, tapeCount, source, ...). Early and late shows
// share a dateKey: key and select find rows by `sid`. No dedupeByDate here.
const adaptFindRow = (row) => {
  const dateKey = row.dateKey;
  return {
    ...row,
    dateKey,
    dateApi: dateKey,
    displayDate: String(dateKey || row.iso || '').substring(0, 10),
    venue: row.venue || 'Unknown',
    location: row.location || 'Unknown',
    length: row.bestTape && row.bestTape.length ? row.bestTape.length : '',
    matched: row.matched || [],
    explain: row.explain || null,
    bestTape: row.bestTape || null,
    tapeCount: row.tapeCount || 0,
  };
};

export const find = (band, q, { limit, cursor } = {}) => {
  const p = toFindParams(q);
  if (limit) p.append('limit', String(clampInt(limit, 1, 100, 50)));
  if (cursor) p.append('cursor', cursor);
  return dsGet(band, '/find', p, 'find', (data) => ({
    total: data.total || 0,
    cursor: data.cursor || null,
    unknownKeys: data.unknownKeys || [],
    rows: (data.rows || []).map(adaptFindRow),
  }));
};

export const findCount = (band, q) =>
  dsGet(band, '/find/count', toFindParams(q, { withSort: false }), 'find', (data) => ({
    n: data.n || 0,
    dropOne: data.dropOne || [],
  }));

// Long pages keep a listening queue full with few calls (RADIO2): Play the
// Matches asks for MATCHES_PAGE shows per call (server max 100), and every
// radio call asks for RADIO_PAGE_MINUTES of music (server: 10-240 minutes,
// at most 40 segments).
export const MATCHES_PAGE = 50;
export const RADIO_PAGE_MINUTES = 150;

// A server from before RADIO2 caps matches at 40 and answers a larger limit
// with a 400. One retry at that old cap keeps Play the Matches working if the
// web ships before the API restarts.
const OLD_MATCHES_MAX = 40;

export const findMatches = (band, q, { cursor, limit } = {}) => {
  const lim = limit ? clampInt(limit, 1, 100, MATCHES_PAGE) : null;
  const call = (n) => {
    const p = toFindParams(q);
    if (n) p.append('limit', String(n));
    if (cursor) p.append('cursor', cursor);
    return dsGet(band, '/find/matches', p, 'find', (data) => ({
      segments: data.segments || [],
      cursor: data.cursor || null,
      skipped: data.skipped || 0,
    }));
  };
  return call(lim).catch((err) => {
    if (err && err.status === 400 && lim && lim > OLD_MATCHES_MAX) return call(OLD_MATCHES_MAX);
    throw err;
  });
};

// seqKeys: one song key, or the keys of a linked run in order (a > b > c)
export const fetchNext = (band, seqKeys, limit = 12) => {
  const keys = (Array.isArray(seqKeys) ? seqKeys : [seqKeys]).filter(Boolean);
  return dsGet(band, '/next', toParams({ seq: keys.join('>'), limit }), 'find');
};

// ---- details ----

export const fetchSong = (band, key) => dsGet(band, '/song/' + encodeURIComponent(key), null, 'song');

// by { sid } or { date: 'YYYY-MM-DD' } -> { shows: [ShowDetail] }
export const fetchShowDetail = (band, { sid, date } = {}) =>
  dsGet(band, '/show', toParams(sid ? { sid } : { date }), 'show', (data) => ({ shows: data.shows || [] }));

export const fetchAlign = (band, tapeId, sid) =>
  dsGet(band, '/tape/' + encodeURIComponent(tapeId) + '/align', toParams({ sid }), 'show');

export const fetchSegment = (band, perfId, land) =>
  dsGet(band, '/segment', toParams({ perf: perfId, land: land ? '1' : '0' }), 'song');

// ---- play ----

// B.9 allows 50 excludes. (Express's qs turns more than 20 repeats of one key
// into an object with digit keys; the server's params.list accepts that form.)
const EXCLUDE_SEND_MAX = 50;

export const surprise = (band, { kind, era, from, to, sbd, exclude, seed } = {}) =>
  dsGet(
    band,
    '/surprise',
    toParams({ kind, era, from, to, sbd: !!sbd, exclude: (exclude || []).slice(0, EXCLUDE_SEND_MAX), seed }),
    'surprise'
  );

// params = { mode, vibe, era, from, to, sbd, song, order, path, land, more, less, seed,
//            minutes (page length, default RADIO_PAGE_MINUTES), n (max segments) }
export const radio = (band, params, cursor) => {
  const r = params || {};
  return dsGet(
    band,
    '/radio',
    toParams({
      mode: r.mode,
      vibe: r.vibe,
      era: r.era,
      from: r.from,
      to: r.to,
      sbd: !!r.sbd,
      song: r.song,
      order: r.order,
      path: r.path,
      land: !!r.land,
      more: (r.more || []).slice(-20),
      less: (r.less || []).slice(-20),
      seed: r.seed,
      minutes: clampInt(r.minutes, 10, 240, RADIO_PAGE_MINUTES),
      n: r.n ? clampInt(r.n, 1, 40, 40) : null,
      cursor: cursor || null,
    }),
    'radio'
  );
};

// ---- Vibe Check ----

export const vibeAsk = (band, text) => {
  // eslint-disable-next-line no-control-regex
  const q = String(text == null ? '' : text).replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, 200).trim();
  if (!q) {
    const e = new Error('Describe what you want to hear first.');
    e.userMessage = e.message;
    e.status = 0;
    e.code = 'empty';
    e.detail = null;
    return Promise.reject(e);
  }
  return dsGet(band, '/vibe/ask', toParams({ q }), 'vibe');
};
